import axios from 'axios';
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'aes' });
const BASE_URL = 
// 'http://localhost:7002/api' 
'https://rentacourt-backend-pg.onrender.com/api';

// Centralized token getter that matches AuthContext
const getAuthToken = () => {
  try {
    // First try SecureLS (both possible keys)
    try {
      let token = ls.get('token');
      if (token) return token;
      
      token = ls.get('authToken');
      if (token) return token;
    } catch (secureError) {
      console.warn('SecureLS token access failed:', secureError.message);
    }
    
    // Fallback to localStorage
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  } catch (error) {
    console.error('Error in getAuthToken:', error);
    return null;
  }
};

// Check if token is expired (same logic as AuthContext)
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decodedToken = JSON.parse(window.atob(base64));
    
    const now = Date.now() / 1000;
    const expiry = decodedToken.exp;
    
    return expiry <= now + 60; // 1 minute buffer
  } catch (e) {
    console.error('Error checking token expiration:', e);
    return true;
  }
};

// Clear all auth data
const clearAuthData = () => {
  try {
    ls.remove('token');
    ls.remove('authToken');
    ls.remove('userData');
  } catch (e) {
    console.warn('SecureLS cleanup failed in request.js');
  }
  
  localStorage.removeItem('authToken');
  localStorage.removeItem('token');
  localStorage.removeItem('userData');
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor for adding auth token
axiosInstance.interceptors.request.use(
  config => {
    const token = getAuthToken();
    
    if (token) {
      // Check if token is expired before making request
      if (isTokenExpired(token)) {
        console.warn('Token expired during request, clearing auth data');
        clearAuthData();
        // Optionally redirect to login or trigger re-authentication
        if (typeof window !== 'undefined') {
          window.location.href = '/admin/login';
        }
        return Promise.reject(new Error('Token expired'));
      }
      
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling auth errors
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.error('Authentication error - clearing tokens and redirecting');
      clearAuthData();
      
      // Only redirect if we're in a browser environment
      if (typeof window !== 'undefined') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

const request = {
  post: async (url, data, requiresAuth = false, isFormData = false) => {
    try {
      const headers = {};
      
      if (requiresAuth) {
        const token = getAuthToken();
        if (!token) {
          console.error('Authentication required but no token found');
          return {
            status: 401,
            data: { success: false, message: 'No authentication token found' }
          };
        }
        
        if (isTokenExpired(token)) {
          console.error('Token expired');
          clearAuthData();
          return {
            status: 401,
            data: { success: false, message: 'Token expired' }
          };
        }
        
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (isFormData) {
        delete headers['Content-Type'];
      }
      
      const response = await axiosInstance.post(url, data, { headers });
      return {
        status: response.status,
        data: response.data
      };
    } catch (error) {
      console.error('POST request error:', error);
      return {
        status: error.response?.status || 500,
        data: error.response?.data || { success: false, message: 'Request failed' }
      };
    }
  },
  
  get: async (url, requiresAuth = false) => {
    try {
      const headers = {};
      
      if (requiresAuth) {
        const token = getAuthToken();
        if (!token) {
          return {
            status: 401,
            data: { success: false, message: 'No authentication token found' }
          };
        }
        
        if (isTokenExpired(token)) {
          console.error('Token expired');
          clearAuthData();
          return {
            status: 401,
            data: { success: false, message: 'Token expired' }
          };
        }
        
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axiosInstance.get(url, { headers });
      return {
        status: response.status,
        data: response.data
      };
    } catch (error) {
      console.error('GET request error:', error);
      return {
        status: error.response?.status || 500,
        data: error.response?.data || { success: false, message: 'Request failed' }
      };
    }
  },
  
  put: async (url, data, requiresAuth = false) => {
    try {
      const headers = {};
      
      if (requiresAuth) {
        const token = getAuthToken();
        if (!token) {
          return {
            status: 401,
            data: { success: false, message: 'No authentication token found' }
          };
        }
        
        if (isTokenExpired(token)) {
          console.error('Token expired');
          clearAuthData();
          return {
            status: 401,
            data: { success: false, message: 'Token expired' }
          };
        }
        
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axiosInstance.put(url, data, { headers });
      return {
        status: response.status,
        data: response.data
      };
    } catch (error) {
      console.error('PUT request error:', error);
      return {
        status: error.response?.status || 500,
        data: error.response?.data || { success: false, message: 'Request failed' }
      };
    }
  },
  
  delete: async (url, requiresAuth = false) => {
    try {
      const headers = {};
      
      if (requiresAuth) {
        const token = getAuthToken();
        if (!token) {
          return {
            status: 401,
            data: { success: false, message: 'No authentication token found' }
          };
        }
        
        if (isTokenExpired(token)) {
          console.error('Token expired');
          clearAuthData();
          return {
            status: 401,
            data: { success: false, message: 'Token expired' }
          };
        }
        
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axiosInstance.delete(url, { headers });
      return {
        status: response.status,
        data: response.data
      };
    } catch (error) {
      console.error('DELETE request error:', error);
      return {
        status: error.response?.status || 500,
        data: error.response?.data || { success: false, message: 'Request failed' }
      };
    }
  },

  patch: async (url, data, requiresAuth = false) => {
    try {
      const headers = {};

      if (requiresAuth) {
        const token = getAuthToken();
        if (!token) {
          return {
            status: 401,
            data: { success: false, message: 'No authentication token found' }
          };
        }

        if (isTokenExpired(token)) {
          clearAuthData();
          return {
            status: 401,
            data: { success: false, message: 'Token expired' }
          };
        }

        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axiosInstance.patch(url, data, { headers });
      return {
        status: response.status,
        data: response.data
      };
    } catch (error) {
      console.error('PATCH request error:', error);
      return {
        status: error.response?.status || 500,
        data: error.response?.data || { success: false, message: 'Request failed' }
      };
    }
  }

};

export default request;